document.addEventListener('DOMContentLoaded', () => {

    'use strict';

    /**
    * Class which creates a sticky in-page navigation in the page top.
    * Used in both default pages as well as in study programmes.
    * Be careful and test thoroughly.
    * @returns a sticky menu
    */
    const runStickyMenu = () => {
        // Abort if sticky criteria are not met.
        const stickyMenu = document.querySelectorAll('.pagetop-large.has-sticky');
        if (!stickyMenu || typeof debounce !== 'function') {
            return;
        }

        let isSticky = false;
        // Set selector for the two different in-page navigations
        let sections;
        if (document.getElementById('toc-numbered')) {
            // For studies doktype
            sections = '.with-number>div[id]';
        } else if (document.getElementById('toc-manual-numbered')) {
            // For numbered table of content content element "Table of content"
            sections = '.ucphcontenttableofcontent_tableofcontent[id]';
        } else {
            // For standard table of content using section index setting in content elements
            sections = 'section>div[id]';
        }

        // Sticky element definitions
        const spy = document.getElementById('spy');
        let items = spy.querySelectorAll('.toc-items>li');
        let header, itemSelector, btn, cloned, translations;
        if (pageLanguage() === 'da') {
            translations = {
                'toc': 'Indholdsfortegnelse',
                'toggle': 'Åbn/luk indholdsfortegnelse'
            }
        } else {
            translations = {
                'toc': 'Table of content',
                'toggle': 'Toggle in-page navigation'
            }
        }

        class Sticky {
            constructor(sticky) {
                this.sticky = sticky;
                this.cloneMenu();
                this.addContentNumbers();
                this.observeElements();
                this.addEventListeners();
            }

            /**
             * Clone in-page menu and use it for sticky menu.
             */
            cloneMenu() {
                let el = document.createElement('div');
                el.setAttribute('id', 'clone-menu');
                el.setAttribute('class', 'sticky-toc');
                el.setAttribute('data-category-content-location', 'Main content');
                const menu = spy.cloneNode(true);
                el.innerHTML = menu.innerHTML;
                // Append to page
                document.body.appendChild(el);
                // Add variables
                cloned = document.getElementById('clone-menu');
                header = cloned.querySelector('.toc-header');
                items = spy.querySelectorAll('.toc-items>li');
                itemSelector = '.toc-items>li>.menu-item>a';
                // Add numbers
                cloned.querySelectorAll('.toc-items>li').forEach((el, i) => {
                    let no = el.querySelector('.no');
                    if (!no) {
                        return;
                    };
                    let number = i + 1;
                    // Prefix with zero if lower than 10.
                    no.textContent = number > 9 ? `${number}` : `0${number}`;
                });
                // Tracking attributes
                this.setDataAttr();
                // Append open/close button
                this.toggleButton();
            }

            /**
             * Create sticky element by checking when scroll is below element.
             */
            createSticky() {
                const options = { rootMargin: '-15px 0px 100px 0px', threshold: [0.999, 0.001] };

                if (!!window.IntersectionObserver) {
                    let observer = new IntersectionObserver((entries, observer) => {
                        entries.forEach(entry => {
                            if (!entry.isIntersecting && window.scrollY > entry.boundingClientRect.bottom) {
                                // Below sticky element
                                isSticky = true;
                                cloned.classList.add('sticky');
                                cloned.classList.remove('open');
                                document.body.classList.add('sticky-menu');
                                this.inPageVisibility('none');
                                btn.classList.replace('icon-close', 'icon-burger');
                            } else {
                                // Above sticky element
                                isSticky = false;
                                header.innerHTML = '';
                                cloned.classList.remove('sticky');
                                document.body.classList.remove('sticky-menu');
                                this.inPageVisibility('block');
                            }
                        });
                    }, options);

                    stickyMenu.forEach(el => {
                        observer.observe(el);
                    });
                }
            }

            /**
             * Check when scrolling past content elements.
             */
            observeElements() {
                let activeMenuItem = null;

                // Iterate through each section element.
                document.querySelectorAll(sections).forEach((section) => {
                    // Check if the content element has a header and an id.
                    const headers = section.querySelector('.content-header');
                    const id = section.getAttribute('id');
                    // Get section position from top of page.
                    const bodyRect = document.body.getBoundingClientRect(),
                        sectionRect = section.getBoundingClientRect(),
                        offset = sectionRect.top - bodyRect.top;

                    if (id && headers) {
                        // Check if the user has scrolled past this header (minus something for extra offset).
                        if (window.scrollY > (offset - 150)) {
                            // If scrolled past the header of the current content element, set the corresponding menu item as active.
                            activeMenuItem = cloned.querySelector(`${itemSelector}[href="#${id}"]`);
                        } else {
                            // Or reset active menu items.
                            cloned.querySelectorAll(itemSelector).forEach((menuItem) => {
                                menuItem.classList.remove('active');
                            });
                        }
                    }
                });

                // Remove active class from all menu items.
                // Or if there's no sticky menu.
                if (activeMenuItem || !isSticky) {
                    cloned.querySelectorAll(itemSelector).forEach((menuItem) => {
                        menuItem.classList.remove('active');
                    });
                }
                // Add active class only to the last identified menu item.
                if (activeMenuItem) {
                    activeMenuItem.classList.add('active');
                }
            }

            /**
             * Find active menu items and display them in the sticky menu.
             * @param state (string) Display property, e.g. 'none' or 'block'.
             */
            inPageVisibility(state) {
                cloned.querySelector('.toc-items').style.display = state;
                // Clear header element.
                header.innerHTML = '';
                if (isSticky) {
                    // Find active anchor
                    let active = cloned.querySelector(`${itemSelector}.active`);
                    if (active) {
                        // Copy active menu item and paste it into the header element.
                        const clone = active.parentElement.parentElement.cloneNode(true);
                        header.innerHTML = clone.innerHTML;

                    } else {
                        // Or set default header text.
                        header.textContent = translations.toc;
                    }
                }
            }

            /**
             * Create and append button in sticky menu (if not included in html),
             * and listen for clicks.
             */
            toggleButton() {
                let b = document.createElement('button');
                b.setAttribute('type', 'button');
                b.setAttribute('id', 'btn-toc');
                b.setAttribute('class', 'btn icon icon-burger');
                b.setAttribute('aria-label', translations.toggle);
                b.setAttribute('data-category-content-presentation', 'Sticky section menu toggle button');
                b.setAttribute('data-category-content-name', 'Open/Close');
                b.setAttribute('aria-expanded', false);
                cloned.querySelector('.toc-toggle').appendChild(b);

                btn = document.getElementById('btn-toc');

                // Add event listener to new button.
                if (btn) {
                    btn.addEventListener('click', () => {
                        // Toggle in-page navigation drop down.
                        cloned.classList.toggle('open');
                        this.dropdownState();
                    });
                }
            }

            /**
            * Toggle in-page navigation dropdown menu state.
            */
            dropdownState() {
                if (cloned.classList.contains('open')) {
                    this.inPageVisibility('block');
                    header.textContent = translations.toc;
                } else {
                    cloned.classList.remove('open');
                    this.inPageVisibility('none');
                }
                this.buttonState();
            };

            /**
             * Toggle button classes when toggling in-page navigation.
             */
            buttonState() {
                if (btn) {
                    // Is closed, set open.
                    if (btn.classList.contains('icon-burger')) {
                        btn.classList.replace('icon-burger', 'icon-close');
                    } else {
                        // Is open, set closed.
                        btn.classList.replace('icon-close', 'icon-burger');
                    }
                    btn.setAttribute('aria-expanded', `${(btn.getAttribute('aria-expanded') !== 'true').toString()}`);
                }
            };

            addContentNumbers() {
                items.forEach((el, i) => {
                    let no = el.querySelector('.no');
                    if (!no) {
                        return;
                    };
                    let number = i + 1;
                    // Prefix with zero if lower than 10.
                    no.textContent = number > 9 ? `${number}` : `0${number}`;
                    // Get id from items anchors.
                    let id = el.querySelector('a').getAttribute('href').replace('#', '');
                    const section = document.getElementById(id);
                    // Add corresponding numbers to sections if they exist.
                    if (section) {
                        // Prefix with zero if lower than 10.
                        section.querySelector('.number').textContent = number > 9 ? `${number}` : `0${number}`;
                    }
                });
            }

            /**
             * Set data attributes.
             */
            setDataAttr() {
                const attr = 'data-category-content-presentation';
                cloned.querySelectorAll('.toc-items>li').forEach((el) => {
                    let a = el.querySelector('a');
                    a.setAttribute(attr, 'Sticky section menu');
                });
            }

            addEventListeners() {
                window.addEventListener('resize', debounce(() => {
                    this.createSticky();
                    this.observeElements();
                }, 120));

                window.addEventListener('scroll', () => {
                    this.createSticky();
                    this.observeElements();
                }, {
                    passive: true
                });
            }
        }
        /**
         * Assign class to element.
         */
        if (stickyMenu) {
            stickyMenu.forEach((stick) => {
                const stickyEl = new Sticky(stick);
            });
        }
    }

    runStickyMenu();
});